import React, { useSyncExternalStore } from 'react';

// SSR-safe "has mounted" store: no setState in effect (avoids react-hooks/set-state-in-effect).
let clientMounted = false;
const mountListeners = new Set<() => void>();

function subscribe(onStoreChange: () => void): () => void {
  mountListeners.add(onStoreChange);
  if (typeof window !== 'undefined' && !clientMounted) {
    queueMicrotask(() => {
      clientMounted = true;
      mountListeners.forEach((l) => l());
    });
  }
  return () => {
    mountListeners.delete(onStoreChange);
  };
}

function getSnapshot(): boolean {
  return clientMounted;
}

function getServerSnapshot(): boolean {
  return false;
}

function useHasMounted(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

interface ClientOnlyProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

/**
 * ClientOnly component prevents hydration mismatches by only rendering children on the client side.
 * This is useful for components that use browser APIs or generate different content on server vs client.
 */
const ClientOnly: React.FC<ClientOnlyProps> = ({ children, fallback = null }) => {
  const hasMounted = useHasMounted();

  if (!hasMounted) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
};

export default ClientOnly;
export { useHasMounted };
